import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { loginPath } from "../lib/portalSlug";
import { useAuth } from "../contexts/AuthContext";
import { sectionKeyForPath } from "../lib/permissions";
import { getNotifLink as getNotifLinkFor } from "../lib/notifLink";
import { useTheme } from "../contexts/ThemeContext";
import { useI18n } from "../contexts/I18nContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import QuickCreateMenu from "./QuickCreateMenu";
import ShortcutsCheatsheet from "./ShortcutsCheatsheet";
import KbHelpDrawer from "./KbHelpDrawer";
import { toast } from "sonner";
import {
  SquaresFour, Folder, ListChecks, Truck, Bell, Gear, SignOut, CaretLeft, CaretRight, CaretDown,
  Sun, Moon, ClipboardText, MagnifyingGlass, ChartBar, X, Heartbeat, Prohibit, Bookmark, ChatCircle, ShieldCheck,
  Lightning, Trophy, CalendarBlank, Robot, Briefcase, PenNib, Info, CheckCircle, Warning, Check,
  SpeakerHigh, SpeakerSlash, BellRinging, ArrowsLeftRight, Buildings, Receipt, FileText, Book, Palette,
  Globe, EnvelopeSimple, Database, Translate, Scales, UsersThree, CreditCard, TrendUp, Sparkle, ChartLineUp, Compass, TreeStructure,
  Megaphone, HandPalm, Gauge, Star, MapPin, Users, Clock, MapTrifold, WhatsappLogo, Monitor, Kanban, CalendarCheck, HardHat, Siren,
} from "@phosphor-icons/react";
import { STAFF_SECTIONS, visibleItemsFor, itemIsActive, flattenItems, MAX_FAVOURITES } from "./navSections";
import MobileNav from "./MobileNav";
import { PortalBrand } from "./portal/PortalBrand";
import { PortalTheme } from "./portal/PortalTheme";
import { useIsMainClient } from "./portal/usePortal";
import { FocusBanner } from "./portal/FamilyFilter";
import PlatformBrand from "./PlatformBrand";
import MonitoringAgent from "./MonitoringAgent";
import TimerWidget from "./TimerWidget";
import MobileBackButton, { useSwipeBack } from "./MobileBackButton";
import MobileFAB from "./MobileFAB";
import { useNavDirection } from "../lib/useNavDirection";
import { useStatusBarTheme } from "../lib/useStatusBarTheme";

// Grouped navigation structure
function AccessRestricted() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="access-restricted">
      <div className="h-14 w-14 rounded-full bg-red-100 dark:bg-red-950/40 flex items-center justify-center mb-4">
        <Prohibit size={28} className="text-red-500" />
      </div>
      <h2 className="text-lg font-semibold">Access restricted</h2>
      <p className="text-sm text-muted-foreground mt-1 max-w-sm">
        Your role does not include access to this section of the portal. Contact your Operations Director if you need it enabled.
      </p>
    </div>
  );
}

const NAV_SECTIONS = [
  {
    id: "client_portal",
    label: "Client Portal",
    roles: ["client"],
    items: [
      { to: "/portal", label: "Dashboard", icon: SquaresFour, roles: ["client"], exact: true },
      { to: "/portal/requests", label: "Job Requests", icon: ClipboardText, roles: ["client"] },
      { to: "/portal/emergencies", label: "Emergencies", icon: Lightning, roles: ["client"] },
      { to: "/portal/jobs", label: "Jobs", icon: Briefcase, roles: ["client"] },
      { to: "/portal/projects", label: "Projects", icon: Kanban, roles: ["client"] },
      { to: "/portal/contracts", label: "Contracts", icon: CalendarCheck, roles: ["client"] },
      { to: "/portal/sub-clients", label: "Sub-clients", icon: TreeStructure, roles: ["client"], mainClientOnly: true },
      { to: "/portal/calendar", label: "Calendar", icon: CalendarBlank, roles: ["client"] },
      { to: "/portal/quotes", label: "Quotes", icon: Receipt, roles: ["client"] },
      { to: "/portal/invoices", label: "Invoices", icon: CreditCard, roles: ["client"] },
      { to: "/portal/files", label: "File Hub", icon: Folder, roles: ["client"] },
      { to: "/portal/messages", label: "Messages", icon: ChatCircle, roles: ["client"] },
    ],
  },
  {
    id: "client_account",
    label: "Account",
    roles: ["client"],
    items: [
      { to: "/portal/properties", label: "Properties", icon: Buildings, roles: ["client"] },
      { to: "/portal/reports", label: "Reports", icon: ChartBar, roles: ["client"] },
      { to: "/portal/team", label: "Team", icon: Users, roles: ["client"] },
      { to: "/notifications", label: "Notifications", icon: Bell, roles: ["client"] },
      { to: "/portal/settings", label: "Settings", icon: Gear, roles: ["client"] },
      { to: "/portal/help", label: "Help", icon: Book, roles: ["client"] },
    ],
  },
  ...STAFF_SECTIONS,
];

// Flat list for mobile bottom nav
const MOBILE_NAV = [
  { to: "/portal", label: "Home", icon: SquaresFour, roles: ["client"] },
  { to: "/portal/requests", label: "Requests", icon: ClipboardText, roles: ["client"] },
  { to: "/portal/jobs", label: "Jobs", icon: Briefcase, roles: ["client"] },
  { to: "/portal/messages", label: "Messages", icon: ChatCircle, roles: ["client"] },
  { to: "/notifications", label: "Alerts", icon: Bell, roles: ["client"] },
  { to: "/", label: "Dashboard", icon: SquaresFour, roles: ["super_admin", "admin", "project_manager", "team_member", "vendor", "stakeholder"] },
  { to: "/my-work", label: "My Work", icon: Briefcase, roles: ["in_house", "vendor_operative"] },
  { to: "/my-work#schedule", label: "Schedule", icon: CalendarBlank, roles: ["in_house", "vendor_operative"] },
  { to: "/my-work/route-planner", label: "Route", icon: MapTrifold, roles: ["in_house", "vendor_operative"] },
  { to: "/my-work/help", label: "Help", icon: Book, roles: ["in_house", "vendor_operative"] },
  { to: "/jobs", label: "Jobs", icon: Briefcase, roles: ["super_admin", "admin", "project_manager", "vendor"] },
  { to: "/messages", label: "Messages", icon: ChatCircle, roles: ["super_admin", "admin", "project_manager", "vendor", "in_house"] },
  { to: "/notifications", label: "Alerts", icon: Bell, roles: ["super_admin", "admin", "project_manager", "team_member", "vendor", "stakeholder", "in_house", "vendor_operative"] },
  { to: "/settings", label: "Settings", icon: Gear, roles: ["super_admin", "admin"] },
];

export default function Layout({ children }) {
  const { user, logout, patchUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, LANGUAGES } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  // Collapsible menu groups: per-user preference from the account (default on); open/closed state remembered per device
  const navCollapsible = user?.ui_prefs?.nav_collapsible !== false;
  const openKey = `necos_nav_open:${user?._id || user?.id || "anon"}`;
  const [openGroups, setOpenGroups] = useState(() => { try { return JSON.parse(localStorage.getItem(openKey) || "{}"); } catch { return {}; } });
  useEffect(() => { try { setOpenGroups(JSON.parse(localStorage.getItem(openKey) || "{}")); } catch { setOpenGroups({}); } }, [openKey]);
  const toggleGroup = (id) => setOpenGroups((prev) => {
    const next = { ...prev, [id]: !prev[id] };
    try { localStorage.setItem(openKey, JSON.stringify(next)); } catch {}
    return next;
  });
  useEffect(() => {
    // The group holding the current page opens itself; everything else stays as the user left it
    const active = NAV_SECTIONS.find((s) => s.collapsible && s.items.some((i) => itemIsActive(i, location) || (i.children || []).some((c) => itemIsActive(c, location))));
    if (active && !openGroups[active.id]) toggleGroup(active.id);
    const t = setTimeout(() => document.querySelector("[data-testid='sidebar'] .nav-link-smooth.active")?.scrollIntoView({ block: "nearest" }), 80);
    return () => clearTimeout(t);
  }, [location.pathname, location.search, location.hash]); // eslint-disable-line react-hooks/exhaustive-deps
  const [unreadCount, setUnreadCount] = useState(0);
  const [vendorMsgUnread, setVendorMsgUnread] = useState(0);
  const [expensesPending, setExpensesPending] = useState(0);
  const [posDrafts, setPosDrafts] = useState(0);
  const [inboxNew, setInboxNew] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifOpenMobile, setNotifOpenMobile] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const prevUnreadRef = useRef(null);
  const audioCtxRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("necos_notif_sound") !== "false");
  const [browserNotifEnabled, setBrowserNotifEnabled] = useState(() => localStorage.getItem("necos_browser_notif") === "true");
  const [isOffline, setIsOffline] = useState(() => typeof navigator !== "undefined" && navigator.onLine === false);

  // Phase 321 — custom role section permissions (admins only)
  const isAdminBase = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const [sectionPerms, setSectionPerms] = useState(null);
  useEffect(() => {
    if (user && isAdminBase) {
      API.get("/permissions/me").then(({ data }) => setSectionPerms(data)).catch(() => {});
    }
  }, [user?._id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps
  const sectionAllowed = (key) => {
    if (!key || !isAdminBase) return true;
    if (!sectionPerms || sectionPerms.full_access) return true;
    return sectionPerms.permissions?.[key] !== false;
  };
  const contentBlocked = isAdminBase && !sectionAllowed(sectionKeyForPath(location.pathname));

  // Phase 233 — iOS-style swipe-from-left-edge to go back
  useSwipeBack();
  // Phase 234 — direction-aware page transitions
  const navDirection = useNavDirection();
  // Phase 235 — live status-bar / theme-color updates
  useStatusBarTheme();

  useEffect(() => {
    const onOnline = () => { setIsOffline(false); try { localStorage.setItem("necos_last_sync_iso", new Date().toISOString()); } catch {} };
    const onOffline = () => setIsOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
  }, []);

  // Web Audio API notification chime
  const playNotifSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;
      // Two-tone chime: C5 then E5
      [523.25, 659.25].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + i * 0.12);
        gain.gain.linearRampToValueAtTime(0.15, now + i * 0.12 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.35);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + i * 0.12);
        osc.stop(now + i * 0.12 + 0.4);
      });
    } catch {}
  }, [soundEnabled]);

  // Browser Notification API
  const showBrowserNotif = useCallback((title, body) => {
    if (!browserNotifEnabled) return;
    if (Notification.permission !== "granted") return;
    try {
      const n = new Notification(title, {
        body,
        icon: "https://necos.io/necos-logo.png",
        badge: "https://necos.io/necos-logo.png",
        tag: "necos-notif",
        renotify: true,
      });
      n.onclick = () => { window.focus(); n.close(); };
      setTimeout(() => n.close(), 6000);
    } catch {}
  }, [browserNotifEnabled]);

  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem("necos_notif_sound", String(next));
      return next;
    });
  };

  const toggleBrowserNotif = async () => {
    if (!browserNotifEnabled) {
      // Enabling — request permission first
      if (Notification.permission === "default") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") { toast.error("Browser notification permission denied"); return; }
      } else if (Notification.permission === "denied") {
        toast.error("Notifications are blocked. Enable them in your browser settings.");
        return;
      }
      setBrowserNotifEnabled(true);
      localStorage.setItem("necos_browser_notif", "true");
      toast.success("Browser notifications enabled");
    } else {
      setBrowserNotifEnabled(false);
      localStorage.setItem("necos_browser_notif", "false");
    }
  };

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await API.get("/notifications/unread-count");
      const newCount = data.count;
      // Detect new notifications (count increased since last check)
      if (prevUnreadRef.current !== null && newCount > prevUnreadRef.current) {
        const diff = newCount - prevUnreadRef.current;
        playNotifSound();
        // Fetch latest notification for the toast / browser notif
        try {
          const { data: notifList } = await API.get("/notifications");
          const latest = notifList.find(n => !n.read);
          if (latest) {
            showBrowserNotif(latest.title, latest.message);
            toast(latest.title, {
              description: latest.message,
              icon: <BellRinging size={16} className="text-primary" />,
              duration: 5000,
            });
          } else {
            toast(`${diff} new notification${diff > 1 ? "s" : ""}`, {
              icon: <BellRinging size={16} className="text-primary" />,
              duration: 4000,
            });
          }
        } catch {
          toast(`${diff} new notification${diff > 1 ? "s" : ""}`, {
            icon: <BellRinging size={16} className="text-primary" />,
            duration: 4000,
          });
        }
      }
      prevUnreadRef.current = newCount;
      setUnreadCount(newCount);
    } catch {}
  }, [playNotifSound, showBrowserNotif]);

  const fetchVendorMsgUnread = useCallback(async () => {
    if (!user?.role) return;
    let total = 0;
    if (user.role === "client") {
      try {
        const { data } = await API.get("/client-hub/me");
        total += Number(data?.counts?.unread_messages || 0);
      } catch {}
    }
    // Supplier thread unread (admins + vendors)
    if (["super_admin", "admin", "project_manager", "vendor"].includes(user.role)) {
      try {
        const { data } = await API.get("/vendor-messages/threads");
        total += data.reduce((sum, t) => sum + (t.unread_count || 0), 0);
      } catch {}
    }
    // Team messages unread (admin team room/DMs + in-house DMs)
    if (["super_admin", "admin", "project_manager", "in_house"].includes(user.role)) {
      try {
        const { data } = await API.get("/team-messages/unread-total");
        total += Number(data?.unread || 0);
      } catch {}
    }
    setVendorMsgUnread(total);
  }, [user?.role]);

  const fetchExpensesPending = useCallback(async () => {
    if (!user?.role) return;
    if (!["super_admin", "admin", "project_manager"].includes(user.role)) return;
    try {
      const { data } = await API.get("/admin/expenses/pending-count");
      setExpensesPending(data.pending || 0);
    } catch {}
  }, [user?.role]);

  const fetchPosDrafts = useCallback(async () => {
    if (!user?.role) return;
    if (!["super_admin", "admin", "project_manager"].includes(user.role)) return;
    try {
      const { data } = await API.get("/in-house/pos/draft-count");
      setPosDrafts(data.count || 0);
    } catch {}
  }, [user?.role]);

  const fetchInboxNew = useCallback(async () => {
    if (!user?.role || !["super_admin", "admin", "project_manager"].includes(user.role)) return;
    try {
      const { data } = await API.get("/inbox/counts");
      setInboxNew(data.new || 0);
    } catch {}
  }, [user?.role]);

  useEffect(() => {
    fetchUnread();
    fetchVendorMsgUnread();
    fetchExpensesPending();
    fetchPosDrafts();
    fetchInboxNew();
    // Fallback polling at 30s (WebSocket handles real-time)
    const interval = setInterval(() => { fetchUnread(); fetchVendorMsgUnread(); fetchExpensesPending(); fetchPosDrafts(); fetchInboxNew(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread, fetchVendorMsgUnread, fetchExpensesPending, fetchPosDrafts, fetchInboxNew]);

  // WebSocket for real-time notifications
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);

  // Background-tab title badge — count of unseen notifications while the tab is hidden
  const bgCountRef = useRef(0);
  const originalTitleRef = useRef(typeof document !== "undefined" ? document.title : "");

  const updateTitle = useCallback((count) => {
    if (typeof document === "undefined") return;
    const base = originalTitleRef.current || "Facilities Management Platform";
    // Strip any existing "(N) " prefix before re-adding
    const stripped = base.replace(/^\(\d+\)\s*/, "");
    originalTitleRef.current = stripped;
    document.title = count > 0 ? `(${count > 99 ? "99+" : count}) ${stripped}` : stripped;
  }, []);

  // Reset the badge when the user comes back to the tab
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => {
      if (!document.hidden) {
        bgCountRef.current = 0;
        updateTitle(0);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    // Also reset on focus (some browsers/PWA)
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [updateTitle]);

  useEffect(() => {
    if (!user?._id) return;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = baseUrl.replace(/^http/, "ws") + "/api/ws/notifications";  // session cookie rides along on the handshake

    let alive = true;
    const connect = () => {
      if (!alive) return;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => {
          // Send keepalive every 25s
          const pingInterval = setInterval(() => { try { ws.send("ping"); } catch {} }, 25000);
          ws.onclose = () => clearInterval(pingInterval);
        };
        ws.onmessage = (evt) => {
          if (evt.data === "pong") return;
          try {
            const msg = JSON.parse(evt.data);
            if (msg.type === "notification" && msg.data) {
              const n = msg.data;
              setUnreadCount(prev => prev + 1);
              playNotifSound();
              showBrowserNotif(n.title, n.message);
              toast(n.title, { description: n.message, icon: <BellRinging size={16} className="text-primary" />, duration: 5000 });
              // Prepend to notifications list if dropdown open
              setNotifications(prev => prev.length > 0 ? [n, ...prev] : prev);
              // Broadcast to interested pages (e.g. dashboard widgets) so they can pulse + refresh
              try { window.dispatchEvent(new CustomEvent("necos:notification", { detail: n })); } catch {}
              // Background tab title badge — increment counter if tab is hidden
              if (typeof document !== "undefined" && document.hidden) {
                bgCountRef.current = (bgCountRef.current || 0) + 1;
                updateTitle(bgCountRef.current);
              }
            }
          } catch {}
        };
        ws.onclose = () => {
          wsRef.current = null;
          if (alive) wsReconnectRef.current = setTimeout(connect, 5000);
        };
        ws.onerror = () => ws.close();
      } catch {}
    };
    connect();

    return () => {
      alive = false;
      if (wsReconnectRef.current) clearTimeout(wsReconnectRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch {} }
    };
  }, [user?._id, playNotifSound, showBrowserNotif]);

  const fetchNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const { data } = await API.get("/notifications");
      setNotifications(data);
    } catch {}
    finally { setNotifLoading(false); }
  }, []);

  useEffect(() => {
    if (notifOpen || notifOpenMobile) fetchNotifications();
  }, [notifOpen, notifOpenMobile, fetchNotifications]);

  const NOTIF_ICON_MAP = {
    task_assigned: ListChecks, task_completed: CheckCircle, task_approved: CheckCircle,
    task_rejected: Warning, work_order_issued: ClipboardText, default: Bell
  };

  const getNotifLink = (n) => getNotifLinkFor(n, user);

  const claimFromNotif = async (n) => {
    try {
      const { data } = await API.post(`/sla/job-requests/${n.entity_id}/action`, {});
      toast.success(data?.message === "Already actioned" ? `Already claimed by ${data.actioned_by_name}` : "Claimed — it's yours");
    } catch {
      toast.error("Could not claim");
    }
    try { await API.put(`/notifications/${n.id}/read`); } catch {}
    fetchNotifications();
  };

  const handleNotifClick = async (n) => {
    if (!n.read) {
      try { await API.put(`/notifications/${n.id}/read`); } catch {}
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    const link = getNotifLink(n);
    if (link) {
      setNotifOpen(false);
      setNotifOpenMobile(false);
      navigate(link);
    }
  };

  const handleMarkAllRead = async () => {
    try { await API.put("/notifications/read-all"); } catch {}
    setNotifications(prev => prev.map(x => ({ ...x, read: true })));
    setUnreadCount(0);
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setShowSearch(true); }
      if (e.key === "Escape") setShowSearch(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    if (showSearch && searchRef.current) searchRef.current.focus();
  }, [showSearch]);

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setSearchResults(null); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await API.get("/search", { params: { q } });
        setSearchResults(data);
      } catch { setSearchResults(null); }
      finally { setSearching(false); }
    }, 300);
  };

  const goTo = (path) => { setShowSearch(false); setSearchQuery(""); setSearchResults(null); navigate(path); };
  const handleLogout = async () => { const to = loginPath(); await logout(); navigate(to); };

  const messagesHref = user?.role === "client" ? "/portal/messages" : "/messages";
  const isClient = user?.role === "client";
  const isMainClient = useIsMainClient(isClient);
  const isAdmin = ["super_admin", "admin", "project_manager"].includes(user?.role);

  const totalResults = searchResults ? (searchResults.projects?.length || 0) + (searchResults.tasks?.length || 0) + (searchResults.vendors?.length || 0) + (searchResults.work_orders?.length || 0) : 0;

  // ── Sidebar links, badges and starred favourites (up to five, saved on the account) ──
  const badges = { notifications: unreadCount, messages: vendorMsgUnread, expenses: expensesPending, pos: posDrafts, inbox: inboxNew };
  const badgeTestIds = { messages: "vendor-msg-unread-badge", expenses: "expenses-pending-badge", pos: "pos-drafts-badge", inbox: "inbox-new-badge" };
  const favourites = Array.isArray(user?.ui_prefs?.nav_favourites) ? user.ui_prefs.nav_favourites : [];
  const allVisibleItems = NAV_SECTIONS.flatMap((s) => flattenItems(visibleItemsFor(s, user, { isMainClient, sectionAllowed: (path) => sectionAllowed(sectionKeyForPath(path)) })));
  const favItems = favourites.map((to) => allVisibleItems.find((i) => i.to === to)).filter(Boolean);
  const toggleFavourite = async (to) => {
    const next = favourites.includes(to) ? favourites.filter((x) => x !== to) : [...favourites, to];
    if (next.length > MAX_FAVOURITES) { toast.error(`You can pin up to ${MAX_FAVOURITES} favourites — unpin one first`); return; }
    const prevPrefs = user?.ui_prefs || {};
    patchUser({ ui_prefs: { ...prevPrefs, nav_favourites: next } });
    try { await API.put("/users/me/ui-prefs", { nav_favourites: next }); }
    catch { patchUser({ ui_prefs: prevPrefs }); toast.error("Could not save your favourites"); }
  };
  const renderLink = (item, depth = 0, testPrefix = "nav-") => {
    const isActive = itemIsActive(item, location);
    const Icon = item.icon;
    const count = item.badge ? badges[item.badge] : 0;
    const pinned = favourites.includes(item.to);
    const slug = item.label.toLowerCase().replace(/\s/g, "-").replace(/&/g, "and");
    return (
      <div key={`${testPrefix}${item.to}`}>
        <div className="relative group/item">
          <Link to={item.to} data-testid={`${testPrefix}${slug}`} title={collapsed ? item.label : undefined}
            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium nav-link-smooth ${!collapsed ? "pr-8" : ""} ${depth && !collapsed ? "ml-5 pl-3 border-l border-border/60 rounded-l-none py-1.5 text-[13px]" : ""}
              ${isActive ? "bg-primary text-primary-foreground active" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
            <Icon size={depth ? 17 : 20} weight={isActive ? "fill" : "regular"} />
            {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
            {!collapsed && count > 0 && (
              <Badge variant="destructive" className="h-5 min-w-5 flex items-center justify-center text-[10px] px-1" data-testid={badgeTestIds[item.badge]}>{count}</Badge>
            )}
          </Link>
          {!collapsed && (
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavourite(item.to); }}
              title={pinned ? "Unpin from Favourites" : "Pin to Favourites"} aria-label={pinned ? `Unpin ${item.label}` : `Pin ${item.label} to Favourites`}
              aria-pressed={pinned} data-testid={`${testPrefix}star-${slug}`}
              className={`absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md flex items-center justify-center transition-opacity
                ${pinned ? "opacity-100 text-amber-500" : `opacity-0 group-hover/item:opacity-100 focus-visible:opacity-100 ${isActive ? "text-primary-foreground/80 hover:text-primary-foreground" : "text-muted-foreground/60 hover:text-amber-500"}`}`}>
              <Star size={14} weight={pinned ? "fill" : "regular"} />
            </button>
          )}
        </div>
        {(item.children || []).map((c) => renderLink(c, depth + 1, testPrefix))}
      </div>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {isOffline && <div className="pwa-offline-pill" data-testid="pwa-offline-pill">● Offline</div>}
      {["super_admin", "admin", "project_manager"].includes(user?.role) && <MonitoringAgent />}
      {isClient && <PortalTheme />}
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex flex-col border-r border-border bg-card transition-all duration-200 ${collapsed ? "w-16" : "w-60 xl:w-64 2xl:w-72"}`} data-testid="sidebar">
        <div className={`flex items-center ${collapsed ? "justify-center px-2" : "gap-2 px-4"} h-14 border-b border-border shrink-0`}>
          {isClient ? <PortalBrand collapsed={collapsed} /> : <PlatformBrand variant={collapsed ? "icon" : "sidebar"} />}
        </div>
        <ScrollArea className="flex-1 py-2">
          <nav className="flex flex-col gap-0.5 px-2">
            {favItems.length > 0 && (
              <div data-testid="nav-favourites" className="mb-1">
                {!collapsed ? (
                  <div className="flex items-center gap-2 px-3 pt-1 pb-1.5">
                    <Star size={11} weight="fill" className="text-amber-500" />
                    <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-muted-foreground/60">Favourites</span>
                    <div className="flex-1 h-px bg-border/40" />
                  </div>
                ) : <div className="flex justify-center py-1" title="Favourites"><Star size={13} weight="fill" className="text-amber-500" /></div>}
                <div className="flex flex-col gap-0.5">{favItems.map((item) => renderLink({ ...item, children: undefined }, 0, "nav-fav-"))}</div>
                <div className="h-px bg-border/40 mx-2 mt-2" />
              </div>
            )}
            {NAV_SECTIONS.map((section) => {
              const visibleItems = visibleItemsFor(section, user, { isMainClient, sectionAllowed: (path) => sectionAllowed(sectionKeyForPath(path)) });
              if (visibleItems.length === 0) return null;
              const groupHasActive = visibleItems.some((i) => itemIsActive(i, location) || (i.children || []).some((c) => itemIsActive(c, location)));
              const groupBadge = visibleItems.reduce((n, i) => n + (i.badge && badges[i.badge] > 0 ? badges[i.badge] : 0), 0);
              const isGroup = !!(section.label && section.collapsible && navCollapsible);
              const isOpen = !isGroup || !!openGroups[section.id];
              const GroupIcon = section.icon || Folder;

              return (
                <div key={section.id} data-testid={`nav-group-${section.id}`} data-open={isGroup ? String(isOpen) : undefined}>
                  {/* Group header: a toggle when collapsible groups are on, otherwise the plain label */}
                  {section.label && !collapsed && (isGroup ? (
                    <button type="button" onClick={() => toggleGroup(section.id)} aria-expanded={isOpen}
                      className={`w-full flex items-center gap-2 px-3 mt-2 py-1.5 rounded-md text-left group/nav ${groupHasActive && !isOpen ? "text-primary" : "text-muted-foreground"} hover:bg-muted hover:text-foreground transition-colors`}
                      data-testid={`nav-section-${section.id}`}>
                      <GroupIcon size={16} weight={groupHasActive ? "fill" : "regular"} />
                      <span className="flex-1 text-[11px] uppercase tracking-[0.14em] font-bold">{section.label}</span>
                      {!isOpen && groupBadge > 0 && <Badge variant="destructive" className="h-4 min-w-4 flex items-center justify-center text-[9px] px-1">{groupBadge}</Badge>}
                      <CaretDown size={12} className={`transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`} />
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 px-3 pt-5 pb-1.5" data-testid={`nav-section-${section.id}`}>
                      <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-muted-foreground/50">{section.label}</span>
                      <div className="flex-1 h-px bg-border/40" />
                    </div>
                  ))}
                  {section.label && collapsed && (isGroup ? (
                    <button type="button" onClick={() => toggleGroup(section.id)} title={section.label} aria-expanded={isOpen}
                      className={`w-full flex items-center justify-center py-2 mt-1 rounded-md ${groupHasActive ? "text-primary" : "text-muted-foreground/70"} hover:bg-muted hover:text-foreground`}
                      data-testid={`nav-section-icon-${section.id}`}>
                      <GroupIcon size={18} weight={groupHasActive ? "fill" : "regular"} />
                    </button>
                  ) : <div className="h-px bg-border/40 mx-2 my-3" />)}
                  {isOpen && (
                    <div className={`flex flex-col gap-0.5 ${isGroup && !collapsed ? "nav-group-open" : ""}`}>
                      {visibleItems.map((item) => renderLink(item))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </ScrollArea>
        <div className="border-t border-border p-2 shrink-0 space-y-1">
          {/* Settings */}
          {["super_admin", "admin"].includes(user?.role) && (
            <Link to="/settings" data-testid="nav-settings">
              <Button variant="ghost" size="sm" className={`w-full justify-start gap-3 ${location.pathname === "/settings" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                <Gear size={18} />
                {!collapsed && <span className="text-xs">Settings</span>}
              </Button>
            </Link>
          )}
          <Button variant="ghost" size="sm" className="w-full justify-start gap-3 text-muted-foreground" onClick={toggleTheme} data-testid="theme-toggle">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            {!collapsed && <span className="text-xs">{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>}
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start gap-3 text-muted-foreground" onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-toggle">
            {collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
            {!collapsed && <span className="text-xs">Collapse</span>}
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="hidden md:flex h-14 border-b border-border bg-card/80 backdrop-blur-xl items-center justify-between px-4 sm:px-6 shrink-0 z-50" data-testid="top-header">
          {isClient ? <div /> : (
          <button onClick={() => setShowSearch(true)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="global-search-trigger">
            <MagnifyingGlass size={18} />
            <span>Search...</span>
            <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
              <span className="text-xs">&#8984;</span>K
            </kbd>
          </button>
          )}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Quick Create — admins / PMs only */}
            {["super_admin", "admin", "project_manager"].includes(user?.role) && (
              <QuickCreateMenu />
            )}
            {/* Work timer */}
            {["super_admin", "admin", "project_manager"].includes(user?.role) && (
              <TimerWidget />
            )}
            {/* Messages icon */}
            <Link to={messagesHref} className="relative" data-testid="header-messages">
              <ChatCircle size={20} className="text-muted-foreground" />
              {vendorMsgUnread > 0 && <span className="absolute -top-1 -right-1.5 h-4 min-w-4 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1" data-testid="header-msg-badge">{vendorMsgUnread}</span>}
            </Link>
            {/* Notifications dropdown */}
            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <button className="relative" data-testid="header-notifications">
                  <Bell size={20} className="text-muted-foreground" />
                  {unreadCount > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold px-1" data-testid="notif-unread-badge">{unreadCount}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 p-0" data-testid="notifications-dropdown">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <h3 className="text-sm font-semibold font-[Chivo]">Notifications</h3>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-[10px] text-primary hover:underline font-medium" data-testid="notif-mark-all-read">
                        Mark all read
                      </button>
                    )}
                    <button
                      onClick={toggleSound}
                      className={`p-1 rounded transition-colors ${soundEnabled ? "text-primary hover:bg-primary/10" : "text-muted-foreground hover:bg-muted"}`}
                      title={soundEnabled ? "Mute notification sound" : "Enable notification sound"}
                      data-testid="notif-sound-toggle"
                    >
                      {soundEnabled ? <SpeakerHigh size={14} /> : <SpeakerSlash size={14} />}
                    </button>
                    <button
                      onClick={toggleBrowserNotif}
                      className={`p-1 rounded transition-colors ${browserNotifEnabled ? "text-primary hover:bg-primary/10" : "text-muted-foreground hover:bg-muted"}`}
                      title={browserNotifEnabled ? "Disable browser notifications" : "Enable browser notifications"}
                      data-testid="notif-browser-toggle"
                    >
                      <BellRinging size={14} />
                    </button>
                  </div>
                </div>
                <ScrollArea className="max-h-[400px]">
                  {notifLoading ? (
                    <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" /></div>
                  ) : notifications.length === 0 ? (
                    <div className="text-center py-10 px-4">
                      <Bell size={24} className="mx-auto text-muted-foreground mb-2" />
                      <p className="text-xs text-muted-foreground">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {notifications.slice(0, 20).map(n => {
                        const Icon = NOTIF_ICON_MAP[n.type] || NOTIF_ICON_MAP.default;
                        const link = getNotifLink(n);
                        return (
                          <button
                            key={n.id}
                            onClick={() => handleNotifClick(n)}
                            className={`w-full text-left flex items-start gap-2.5 px-4 py-3 transition-colors hover:bg-muted/50 ${!n.read ? "bg-primary/5" : ""}`}
                            data-testid={`notif-item-${n.id}`}
                          >
                            <Icon size={16} className={`mt-0.5 shrink-0 ${!n.read ? "text-primary" : "text-muted-foreground"}`} weight="duotone" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className={`text-xs font-medium truncate ${n.read ? "text-muted-foreground" : ""}`}>{n.title}</span>
                                {!n.read && <span className="shrink-0 h-1.5 w-1.5 rounded-full bg-primary" />}
                              </div>
                              <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{n.message}</p>
                              <div className="flex items-center justify-between mt-1">
                                <p className="text-[10px] text-muted-foreground/60">{timeAgo(n.created_at)}</p>
                                {n.type === "unclaimed_request" && n.entity_id && (
                                  <span role="button" onClick={(e) => { e.stopPropagation(); claimFromNotif(n); }}
                                        className="inline-flex items-center px-2 py-0.5 rounded bg-red-600 text-white text-[10px] font-semibold hover:bg-red-700"
                                        data-testid={`notif-claim-${n.id}`}>
                                    Claim
                                  </span>
                                )}
                              </div>
                            </div>
                            {link && <span className="text-muted-foreground/40 mt-0.5 shrink-0"><CaretRight size={12} /></span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
                <div className="border-t border-border px-4 py-2.5">
                  <button
                    onClick={() => { setNotifOpen(false); navigate("/notifications"); }}
                    className="w-full text-center text-xs text-primary hover:underline font-medium"
                    data-testid="notif-view-all"
                  >
                    View all notifications
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">{user?.name?.charAt(0)?.toUpperCase()}</div>
              {user && <div className="text-right hidden sm:block"><p className="text-xs font-medium leading-none">{user.name}</p><p className="text-[10px] text-muted-foreground capitalize">{user.role === "vendor_operative" ? "Supplier operative" : user.role?.replace("_", " ")}</p></div>}
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn"><SignOut size={18} /></Button>
          </div>
        </header>
        {isClient && isMainClient && <FocusBanner />}

        <main className="flex-1 overflow-auto pb-[calc(6.5rem+env(safe-area-inset-bottom))] md:pb-0 pwa-no-overscroll"><div className={`p-4 sm:p-6 lg:p-8 pwa-page ${navDirection === "back" ? "pwa-page-back" : ""}`} key={location.pathname}>{contentBlocked ? <AccessRestricted /> : children}</div></main>

        {/* Phase 235 — Floating Action Button (mobile only) */}
        <MobileFAB user={user} />

        {/* Phase 242 — Mobile actions bar (relocated from top to bottom for iOS reachability) */}
        <div
          className="md:hidden fixed left-0 right-0 h-12 bg-card/95 backdrop-blur-xl border-t border-border flex items-center justify-between px-3 z-50 mobile-actions-bar"
          style={{ bottom: "calc(3.5rem + env(safe-area-inset-bottom))" }}
          data-testid="mobile-actions-bar"
        >
          <div className="flex items-center gap-1 min-w-0">
            <MobileNav
              sections={NAV_SECTIONS}
              favourites={favourites}
              user={user}
              counts={{ unreadCount, vendorMsgUnread, expensesPending }}
              theme={theme}
              onToggleTheme={toggleTheme}
              onLogout={logout}
            />
            <MobileBackButton />
            <Link to="/" className="flex items-center gap-1.5 min-w-0" data-testid="mobile-home-link">
              {isClient ? <PortalBrand mobile /> : <PlatformBrand variant="mobile" />}
            </Link>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={() => setShowSearch(true)} className="p-1.5 -m-1.5" aria-label="Search" data-testid="mobile-search-trigger">
              <MagnifyingGlass size={20} className="text-muted-foreground" />
            </button>
            <Link to={messagesHref} className="relative p-1.5 -m-1.5" aria-label="Messages" data-testid="mobile-messages-trigger">
              <ChatCircle size={20} className="text-muted-foreground" />
              {vendorMsgUnread > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1">{vendorMsgUnread}</span>}
            </Link>
            <Popover open={notifOpenMobile} onOpenChange={setNotifOpenMobile}>
              <PopoverTrigger asChild>
                <button className="relative p-1.5 -m-1.5" aria-label="Notifications" data-testid="mobile-notifications-trigger">
                  <Bell size={20} className="text-muted-foreground" />
                  {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold px-1">{unreadCount}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" align="end" sideOffset={8} className="w-[calc(100vw-1.5rem)] max-w-sm p-0 mr-3" data-testid="mobile-notifications-dropdown">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <h3 className="text-sm font-semibold font-[Chivo]">Notifications</h3>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-[10px] text-primary hover:underline font-medium" data-testid="mobile-notif-mark-all-read">
                        Mark all read
                      </button>
                    )}
                  </div>
                </div>
                <ScrollArea className="max-h-[60vh]">
                  {notifLoading ? (
                    <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" /></div>
                  ) : notifications.length === 0 ? (
                    <div className="text-center py-10 px-4">
                      <Bell size={24} className="mx-auto text-muted-foreground mb-2" />
                      <p className="text-xs text-muted-foreground">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {notifications.slice(0, 20).map(n => {
                        const Icon = NOTIF_ICON_MAP[n.type] || NOTIF_ICON_MAP.default;
                        const link = getNotifLink(n);
                        return (
                          <button
                            key={n.id}
                            onClick={() => handleNotifClick(n)}
                            className={`w-full text-left flex items-start gap-2.5 px-4 py-3 transition-colors hover:bg-muted/50 ${!n.read ? "bg-primary/5" : ""}`}
                            data-testid={`mobile-notif-item-${n.id}`}
                          >
                            <Icon size={16} className={`mt-0.5 shrink-0 ${!n.read ? "text-primary" : "text-muted-foreground"}`} weight="duotone" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className={`text-xs font-medium truncate ${n.read ? "text-muted-foreground" : ""}`}>{n.title}</span>
                                {!n.read && <span className="shrink-0 h-1.5 w-1.5 rounded-full bg-primary" />}
                              </div>
                              <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{n.message}</p>
                              <div className="flex items-center justify-between mt-1">
                                <p className="text-[10px] text-muted-foreground/60">{timeAgo(n.created_at)}</p>
                                {n.type === "unclaimed_request" && n.entity_id && (
                                  <span role="button" onClick={(e) => { e.stopPropagation(); claimFromNotif(n); }}
                                        className="inline-flex items-center px-2 py-0.5 rounded bg-red-600 text-white text-[10px] font-semibold hover:bg-red-700"
                                        data-testid={`mobile-notif-claim-${n.id}`}>
                                    Claim
                                  </span>
                                )}
                              </div>
                            </div>
                            {link && <span className="text-muted-foreground/40 mt-0.5 shrink-0"><CaretRight size={12} /></span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
                <div className="border-t border-border px-4 py-2.5">
                  <button
                    onClick={() => { setNotifOpenMobile(false); navigate("/notifications"); }}
                    className="w-full text-center text-xs text-primary hover:underline font-medium"
                    data-testid="mobile-notif-view-all"
                  >
                    View all notifications
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            <button onClick={handleLogout} className="p-1.5 -m-1.5 text-muted-foreground hover:text-destructive" aria-label="Logout" data-testid="mobile-logout-btn">
              <SignOut size={18} />
            </button>
          </div>
        </div>

        {/* Mobile Bottom Navigation */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 h-14 bg-card border-t border-border flex items-stretch z-50 mobile-bottom-nav pwa-safe-bottom" data-testid="mobile-bottom-nav">
          {MOBILE_NAV.filter(item => item.roles.includes(user?.role) && sectionAllowed(sectionKeyForPath(item.to.split("#")[0]))).slice(0, 5).map((item) => {
            const [itemPath, itemHash] = item.to.split("#");
            const currentHash = location.hash.replace("#", "");
            const isActive = itemHash
              ? (location.pathname === itemPath && currentHash === itemHash)
              : (itemPath === "/"
                  ? (location.pathname === "/" && !location.hash)
                  : (location.pathname.startsWith(itemPath) && !location.hash));
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="relative">
                  <Icon size={20} weight={isActive ? "fill" : "regular"} />
                  {item.label === "Alerts" && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-2 h-3.5 min-w-3.5 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[8px] font-bold px-0.5">{unreadCount}</span>
                  )}
                  {item.label === "Messages" && vendorMsgUnread > 0 && (
                    <span className="absolute -top-1 -right-2 h-3.5 min-w-3.5 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[8px] font-bold px-0.5">{vendorMsgUnread}</span>
                  )}
                </div>
                <span className="text-[9px] font-medium leading-none">{item.label.length > 8 ? item.label.slice(0, 7) + "." : item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Global Search Dialog */}
      <Dialog open={showSearch} onOpenChange={setShowSearch}>
        <DialogContent className="max-w-lg p-0 gap-0" data-testid="global-search-dialog">
          <div className="flex items-center gap-3 px-4 border-b border-border">
            <MagnifyingGlass size={18} className="text-muted-foreground shrink-0" />
            <Input ref={searchRef} value={searchQuery} onChange={(e) => handleSearch(e.target.value)} placeholder="Search projects, tasks, suppliers, works orders..."
              className="border-0 shadow-none focus-visible:ring-0 h-12 text-sm" data-testid="global-search-input" />
            {searchQuery && <button onClick={() => { setSearchQuery(""); setSearchResults(null); }} className="shrink-0"><X size={16} className="text-muted-foreground" /></button>}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {searching && <p className="text-center py-6 text-sm text-muted-foreground">Searching...</p>}
            {searchResults && !searching && totalResults === 0 && <p className="text-center py-6 text-sm text-muted-foreground">No results for "{searchQuery}"</p>}
            {searchResults && !searching && totalResults > 0 && (
              <div className="py-2">
                {searchResults.projects?.length > 0 && (
                  <div>
                    <p className="px-4 py-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Projects</p>
                    {searchResults.projects.map(p => (
                      <button key={p.id} onClick={() => goTo(`/projects/${p.id}`)} className="w-full text-left px-4 py-2 hover:bg-muted/50 flex items-center justify-between" data-testid={`search-project-${p.id}`}>
                        <div><p className="text-sm font-medium">{p.name}</p><p className="text-[10px] text-muted-foreground">{p.reference}</p></div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded status-${p.status}`}>{p.status?.replace(/_/g," ")}</span>
                      </button>
                    ))}
                  </div>
                )}
                {searchResults.tasks?.length > 0 && (
                  <div>
                    <p className="px-4 py-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Tasks</p>
                    {searchResults.tasks.map(t => (
                      <button key={t.id} onClick={() => goTo(`/projects/${t.project_id}/tasks/${t.id}`)} className="w-full text-left px-4 py-2 hover:bg-muted/50 flex items-center justify-between" data-testid={`search-task-${t.id}`}>
                        <div><p className="text-sm font-medium">{t.title}</p><p className="text-[10px] text-muted-foreground">{t.reference} | {t.project_name}</p></div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded status-${t.status}`}>{t.status?.replace(/_/g," ")}</span>
                      </button>
                    ))}
                  </div>
                )}
                {searchResults.vendors?.length > 0 && (
                  <div>
                    <p className="px-4 py-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Suppliers</p>
                    {searchResults.vendors.map(v => (
                      <button key={v.id} onClick={() => goTo("/vendors")} className="w-full text-left px-4 py-2 hover:bg-muted/50" data-testid={`search-vendor-${v.id}`}>
                        <p className="text-sm font-medium">{v.company_name}</p><p className="text-[10px] text-muted-foreground">{v.trade}</p>
                      </button>
                    ))}
                  </div>
                )}
                {searchResults.work_orders?.length > 0 && (
                  <div>
                    <p className="px-4 py-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Works Orders</p>
                    {searchResults.work_orders.map(w => (
                      <button key={w.id} onClick={() => goTo("/work-orders")} className="w-full text-left px-4 py-2 hover:bg-muted/50" data-testid={`search-wo-${w.id}`}>
                        <p className="text-sm font-medium">{w.wo_number}</p><p className="text-[10px] text-muted-foreground">{w.project_name}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!searchResults && !searching && <p className="text-center py-8 text-xs text-muted-foreground">Type to search across all entities</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Global keyboard shortcuts cheatsheet (press ?) */}
      <ShortcutsCheatsheet />

      {/* Phase 287 — Contextual help drawer (floating ? button) */}
      <KbHelpDrawer />
    </div>
  );
}
